import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, FileText, Calendar, CreditCard, Loader2 } from "lucide-react";
import { format } from "date-fns";

interface ACHRecord {
  id: string;
  date: Date;
  invoiceCount: number;
  totalAmount: number;
  fileName: string;
  content: string;
}

interface ACHHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  achHistory: ACHRecord[];
  readyCount: number;
  isGenerating: boolean;
  onGenerate: () => void;
}

export function ACHHistoryModal({
  open,
  onOpenChange,
  achHistory,
  readyCount,
  isGenerating,
  onGenerate,
}: ACHHistoryModalProps) {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const handleDownload = (record: ACHRecord) => {
    const blob = new Blob([record.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = record.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Generate ACH & Pay Suppliers
          </DialogTitle>
          <DialogDescription>
            {readyCount > 0
              ? `${readyCount} invoice(s) ready for payment`
              : "No invoices ready for payment"}
          </DialogDescription>
        </DialogHeader>

        {/* Generate CTA */}
        <Button
          className="w-full gradient-primary text-primary-foreground shadow-primary"
          disabled={isGenerating || readyCount === 0}
          onClick={onGenerate}
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" /> Generate ACH & Pay {readyCount} Invoice(s)
            </>
          )}
        </Button>

        {/* History */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-primary" />
            Previous ACH Files
          </h3>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {achHistory.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 text-sm">
                No ACH files generated yet
              </p>
            ) : (
              achHistory.map((record) => (
                <div key={record.id} className="p-3 bg-muted rounded-lg flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {format(record.date, "MMM d, yyyy h:mm a")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {record.invoiceCount} invoice(s) • {formatCurrency(record.totalAmount)}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleDownload(record)}>
                    <Download className="w-3.5 h-3.5 mr-1" />
                    Download
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
